import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileCTA from '@/components/MobileCTA';
import SEO from '@/components/SEO';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { MapPin, Clock, Phone, Navigation, Search, CheckCircle, Truck, Star, Users, Building, Home, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ServiceArea {
  id: string;
  name: string;
  zipCodes: string[];
  deliveryTime: string;
  deliveryTimeEs: string;
  description: string;
  descriptionEs: string;
  featured: boolean;
  distance: string;
  population: string;
}

const AreasPage = () => {
  const { language } = useLanguage();
  const [searchZip, setSearchZip] = useState('');
  const [searchResult, setSearchResult] = useState<ServiceArea | null>(null);

  const serviceAreas: ServiceArea[] = [
    {
      id: 'the-woodlands',
      name: 'The Woodlands',
      zipCodes: ['77380', '77381', '77382', '77384', '77385', '77386', '77387', '77393'],
      deliveryTime: 'Delivery Available',
      deliveryTimeEs: 'Mismo Día',
      description: 'Our flagship service area with comprehensive delivery and installation services.',
      descriptionEs: 'Nuestra área de servicio principal con servicios integrales de entrega e instalación.',
      featured: true,
      distance: '15 min',
      population: '114,000+'
    },
    {
      id: 'cypress',
      name: 'Cypress',
      zipCodes: ['77429', '77433', '77065', '77070', '77095'],
      deliveryTime: 'Delivery Available',
      deliveryTimeEs: 'Mismo Día',
      description: 'Rapid delivery service covering all Cypress neighborhoods and communities.',
      descriptionEs: 'Servicio de entrega rápida cubriendo todos los vecindarios y comunidades de Cypress.',
      featured: true,
      distance: '20 min',
      population: '200,000+'
    },
    {
      id: 'magnolia',
      name: 'Magnolia',
      zipCodes: ['77354', '77355'],
      deliveryTime: 'Delivery Available',
      deliveryTimeEs: 'Mismo Día',
      description: 'Local delivery from our nearby facility with premium service.',
      descriptionEs: 'Entrega local desde nuestra instalación cercana con servicio premium.',
      featured: true,
      distance: '10 min',
      population: '25,000+'
    },
    {
      id: 'conroe',
      name: 'Conroe',
      zipCodes: ['77301', '77302', '77303', '77304', '77305', '77318', '77384'],
      deliveryTime: 'Next Day',
      deliveryTimeEs: 'Día Siguiente',
      description: 'Historic charm meets modern growth with diverse landscaping opportunities from lakefront estates to downtown revitalization projects.',
      descriptionEs: 'Encanto histórico se encuentra con crecimiento moderno con diversas oportunidades de paisajismo desde fincas frente al lago hasta proyectos de revitalización del centro.',
      featured: false,
      distance: '25 min',
      population: '95,000+'
    },
    {
      id: 'spring',
      name: 'Spring',
      zipCodes: ['77373', '77379', '77388', '77389'],
      deliveryTime: 'Delivery Available',
      deliveryTimeEs: 'Mismo Día',
      description: 'Vibrant community with diverse neighborhoods offering exceptional landscaping opportunities for families and businesses.',
      descriptionEs: 'Comunidad vibrante con vecindarios diversos ofreciendo oportunidades excepcionales de paisajismo para familias y negocios.',
      featured: false,
      distance: '18 min',
      population: '85,000+'
    },
    {
      id: 'tomball',
      name: 'Tomball',
      zipCodes: ['77375', '77377'],
      deliveryTime: 'Next Day',
      deliveryTimeEs: 'Día Siguiente',
      description: 'Small-town heritage with modern growth, featuring historic downtown charm and new residential developments.',
      descriptionEs: 'Herencia de pueblo pequeño con crecimiento moderno, presentando encanto histórico del centro y nuevos desarrollos residenciales.',
      featured: false,
      distance: '30 min',
      population: '45,000+'
    },
    {
      id: 'montgomery',
      name: 'Montgomery',
      zipCodes: ['77316', '77356'],
      deliveryTime: 'Next Day',
      deliveryTimeEs: 'Día Siguiente',
      description: 'Historic town with deep Texas roots offering unique landscaping opportunities that honor heritage while meeting modern needs.',
      descriptionEs: 'Pueblo histórico con raíces profundas de Texas ofreciendo oportunidades únicas de paisajismo que honran la herencia mientras satisfacen necesidades modernas.',
      featured: false,
      distance: '35 min',
      population: '15,000+'
    },
    {
      id: 'pinehurst',
      name: 'Pinehurst',
      zipCodes: ['77362'],
      deliveryTime: 'Delivery Available',
      deliveryTimeEs: 'Mismo Día',
      description: 'Charming residential community with tree-lined streets and family-friendly atmosphere, perfect for custom landscaping solutions.',
      descriptionEs: 'Comunidad residencial encantadora con calles arboladas y atmósfera familiar, perfecta para soluciones de paisajismo personalizado.',
      featured: false,
      distance: '22 min',
      population: '8,000+'
    }
  ];

  const handleZipSearch = () => {
    const area = serviceAreas.find(area => 
      area.zipCodes.includes(searchZip.trim())
    );
    setSearchResult(area || null);
  };

  const featuredAreas = serviceAreas.filter(area => area.featured);
  const otherAreas = serviceAreas.filter(area => !area.featured);

  const seoTitle = language === 'en' 
    ? 'Service Areas Montgomery County TX | Sod & Landscaping Delivery'
    : 'Áreas de Servicio Condado Montgomery TX | Entrega Césped y Paisajismo';
  
  const seoDescription = language === 'en'
    ? 'The Grass Depot serves Montgomery County including The Woodlands, Cypress, Magnolia, Conroe, Spring & more. Delivery available throughout Montgomery County. Check if we serve your area.'
    : 'The Grass Depot sirve el Condado Montgomery incluyendo The Woodlands, Cypress, Magnolia, Conroe, Spring y más. Entrega disponible en todo el Condado Montgomery. Verifica si servimos tu área.';

  const seoKeywords = language === 'en'
    ? 'Montgomery County landscaping delivery, The Woodlands sod delivery, Cypress lawn care, Magnolia landscaping, Conroe grass delivery, Spring mulch delivery'
    : 'entrega paisajismo Condado Montgomery, entrega césped The Woodlands, cuidado césped Cypress, paisajismo Magnolia, entrega césped Conroe, entrega mantillo Spring';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "The Grass Depot LLC",
    "serviceArea": serviceAreas.map(area => ({
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "addressLocality": area.name,
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={`/${language}/areas`}
        structuredData={structuredData}
        language={language}
        breadcrumbs={[
          { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
          { name: language === 'en' ? 'Service Areas' : 'Áreas de Servicio', url: `/${language}/areas` },
        ]}
      />
      <Header />
      
      <main>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-natural text-white">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
            {language === 'en' ? 'Areas We Serve' : 'Áreas que Servimos'}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 animate-fade-in">
            {language === 'en'
              ? 'Professional landscaping services across Montgomery County and surrounding areas'
              : 'Servicios profesionales de paisajismo en el Condado de Montgomery y áreas circundantes'
            }
          </p>
          
          {/* Zip Code Search */}
          <div className="max-w-md mx-auto animate-scale-in">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder={language === 'en' ? 'Enter your ZIP code' : 'Ingresa tu código postal'}
                value={searchZip}
                onChange={(e) => setSearchZip(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/70"
                maxLength={5}
              />
              <Button 
                variant="secondary" 
                onClick={handleZipSearch}
                className="px-6 hover-scale"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
            
            {searchResult && (
              <Card className="mt-4 bg-white/10 border-white/20 backdrop-blur-sm animate-fade-in">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 text-white">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <span className="font-medium">
                      {language === 'en' ? 'Yes! We serve' : '¡Sí! Servimos'} {searchResult.name}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-white/80">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{language === 'en' ? searchResult.deliveryTime : searchResult.deliveryTimeEs}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Truck className="h-4 w-4" />
                      <span>{searchResult.distance} {language === 'en' ? 'from depot' : 'del depósito'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {searchZip && !searchResult && searchZip.length === 5 && (
              <Card className="mt-4 bg-white/10 border-white/20 backdrop-blur-sm animate-fade-in">
                <CardContent className="p-4">
                  <div className="text-white text-center">
                    <p className="mb-2">
                      {language === 'en' 
                        ? 'We don\'t currently serve this area, but we\'re expanding!' 
                        : 'Actualmente no servimos esta área, ¡pero nos estamos expandiendo!'
                      }
                    </p>
                    <Button variant="secondary" size="sm" asChild>
                      <Link to={`/${language}/${language === 'en' ? 'contact' : 'contacto'}`}>
                        {language === 'en' ? 'Contact Us' : 'Contáctanos'}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Featured Service Areas */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Primary Service Areas' : 'Áreas de Servicio Principales'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'Delivery available throughout Montgomery County and premium service in our core coverage areas'
                : 'Entrega disponible en todo el Condado Montgomery y servicio premium en nuestras áreas principales de cobertura'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredAreas.map((area, index) => (
              <Card key={area.id} className="card-hover border-2 border-primary/20 animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-xl">{area.name}</CardTitle>
                    <Badge className="bg-primary animate-pulse-glow">
                      {language === 'en' ? 'Featured' : 'Destacado'}
                    </Badge>
                  </div>
                  <CardDescription>
                    {language === 'en' ? area.description : area.descriptionEs}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-primary" />
                        <span className="font-medium">
                          {language === 'en' ? 'Delivery:' : 'Entrega:'}
                        </span>
                      </div>
                      <span className="text-green-600 font-medium">
                        {language === 'en' ? area.deliveryTime : area.deliveryTimeEs}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <Navigation className="h-4 w-4 text-primary" />
                        <span className="font-medium">
                          {language === 'en' ? 'Distance:' : 'Distancia:'}
                        </span>
                      </div>
                      <span>{area.distance}</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        <span className="font-medium">
                          {language === 'en' ? 'Population:' : 'Población:'}
                        </span>
                      </div>
                      <span>{area.population}</span>
                    </div>
                    
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground mb-2">
                        {language === 'en' ? 'ZIP Codes:' : 'Códigos Postales:'}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {area.zipCodes.slice(0, 4).map((zip) => (
                          <Badge key={zip} variant="outline" className="text-xs hover-scale">
                            {zip}
                          </Badge>
                        ))}
                        {area.zipCodes.length > 4 && (
                          <Badge variant="outline" className="text-xs hover-scale">
                            +{area.zipCodes.length - 4}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t space-y-2">
                    <Button variant="outline" className="w-full hover-scale" asChild>
                      <Link to={`/${language}/areas/${area.id}`}>
                        {language === 'en' ? 'View Local Services' : 'Ver Servicios Locales'}
                      </Link>
                    </Button>
                    <Button variant="ghost" className="w-full text-xs" asChild>
                      <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
                        {language === 'en' ? 'Get Quote' : 'Cotizar'}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Other Service Areas */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Additional Service Areas' : 'Áreas de Servicio Adicionales'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'en'
                ? 'Extended coverage areas with next-day delivery available'
                : 'Áreas de cobertura extendida con entrega al día siguiente disponible'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {otherAreas.map((area, index) => (
              <Card key={area.id} className="card-hover border-2 border-secondary/20 animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-xl story-link">
                      <Link to={`/${language}/areas/${area.id}`} className="hover:text-primary transition-colors">
                        {area.name}
                      </Link>
                    </CardTitle>
                    <Badge variant="secondary" className="hover-scale">
                      {language === 'en' ? 'Extended Coverage' : 'Cobertura Extendida'}
                    </Badge>
                  </div>
                  <CardDescription className="text-base">
                    {language === 'en' ? area.description : area.descriptionEs}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-primary" />
                        <span className="font-medium">
                          {language === 'en' ? 'Delivery:' : 'Entrega:'}
                        </span>
                      </div>
                      <span className="text-blue-600 font-medium">
                        {language === 'en' ? area.deliveryTime : area.deliveryTimeEs}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <Navigation className="h-4 w-4 text-primary" />
                        <span className="font-medium">
                          {language === 'en' ? 'Distance:' : 'Distancia:'}
                        </span>
                      </div>
                      <span>{area.distance}</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-primary" />
                        <span className="font-medium">
                          {language === 'en' ? 'Population:' : 'Población:'}
                        </span>
                      </div>
                      <span>{area.population}</span>
                    </div>
                    
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground mb-2">
                        {language === 'en' ? 'ZIP Codes:' : 'Códigos Postales:'}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {area.zipCodes.slice(0, 4).map((zip) => (
                          <Badge key={zip} variant="outline" className="text-xs hover-scale">
                            {zip}
                          </Badge>
                        ))}
                        {area.zipCodes.length > 4 && (
                          <Badge variant="outline" className="text-xs hover-scale">
                            +{area.zipCodes.length - 4}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t space-y-2">
                    <Button variant="outline" className="w-full hover-scale" asChild>
                      <Link to={`/${language}/areas/${area.id}`}>
                        {language === 'en' ? 'View Local Services' : 'Ver Servicios Locales'}
                      </Link>
                    </Button>
                    <Button variant="ghost" className="w-full text-xs" asChild>
                      <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
                        {language === 'en' ? 'Get Quote' : 'Cotizar'}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {language === 'en' ? 'Why Choose Our Service?' : '¿Por Qué Elegir Nuestro Servicio?'}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center p-6 card-hover animate-fade-in">
              <div className="w-16 h-16 bg-gradient-natural rounded-full flex items-center justify-center mx-auto mb-4 animate-float">
                <Truck className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {language === 'en' ? 'Fast Delivery' : 'Entrega Rápida'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'en'
                  ? 'Delivery available throughout Montgomery County, with priority service in primary areas'
                  : 'Entrega disponible en todo el Condado Montgomery, con servicio prioritario en áreas principales'
                }
              </p>
            </Card>

            <Card className="text-center p-6 card-hover animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="w-16 h-16 bg-gradient-natural rounded-full flex items-center justify-center mx-auto mb-4 animate-float" style={{ animationDelay: '0.5s' }}>
                <Star className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {language === 'en' ? 'Local Expertise' : 'Experiencia Local'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'en'
                  ? 'Deep knowledge of Montgomery County soil and climate conditions'
                  : 'Conocimiento profundo del suelo y condiciones climáticas del Condado Montgomery'
                }
              </p>
            </Card>

            <Card className="text-center p-6 card-hover animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="w-16 h-16 bg-gradient-natural rounded-full flex items-center justify-center mx-auto mb-4 animate-float" style={{ animationDelay: '1s' }}>
                <Phone className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {language === 'en' ? 'Personal Service' : 'Servicio Personal'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'en'
                  ? 'Family-owned business committed to customer satisfaction'
                  : 'Negocio familiar comprometido con la satisfacción del cliente'
                }
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-natural text-white">
        <div className="container mx-auto px-6 text-center animate-fade-in">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            {language === 'en' ? 'Ready to Get Started?' : '¿Listo para Comenzar?'}
          </h2>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Contact us today for a free quote and see why we\'re Montgomery County\'s trusted landscaping partner'
              : 'Contáctanos hoy para una cotización gratis y ve por qué somos el socio de paisajismo de confianza del Condado Montgomery'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
              <Button variant="secondary" size="lg" className="btn-pulse hover-scale">
                {language === 'en' ? 'Get Free Quote' : 'Cotización Gratis'}
              </Button>
            </Link>
            <a href="tel:9363200763">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-foreground hover-scale">
                {language === 'en' ? 'Call Now' : 'Llamar Ahora'}
              </Button>
            </a>
          </div>
        </div>
      </section>
      </main>
      
      <Footer />
      <MobileCTA />
    </div>
  );
};

export default AreasPage;
